/**
 * Vento na cena.
 *
 * Convenção meteorológica: a direção é DE ONDE o vento vem, em graus a partir
 * do norte, no sentido horário (vento "de leste" = 90°). É como a biruta, a
 * estação meteorológica e o boletim falam, e é o que o aluno vai ouvir no
 * rádio.
 *
 * Referencial da cena: norte = −Z, leste = +X (vista de cima com o norte para
 * cima da tela).
 */

export interface Vento {
  /** De onde o vento vem, graus a partir do norte, sentido horário (0–360). */
  direcaoGraus: number
  /** Velocidade a 10 m de altura, m/s. */
  velocidadeMS: number
}

export const VENTO_PADRAO: Vento = { direcaoGraus: 90, velocidadeMS: 3 }

export const normalizarGraus = (graus: number): number => ((graus % 360) + 360) % 360

/** Vetor unitário no plano XZ apontando PARA ONDE o vento sopra (a favor do vento). */
export function sentidoAFavorDoVento(direcaoGraus: number): readonly [number, number] {
  const rad = (normalizarGraus(direcaoGraus) * Math.PI) / 180
  // vem do norte (0°) → sopra para o sul (+Z); vem do leste (90°) → sopra para o oeste (−X)
  return [-Math.sin(rad), Math.cos(rad)]
}

/** Rotação em Y (rad) que alinha o eixo +Z local a favor do vento. */
export function rotacaoYAFavorDoVento(direcaoGraus: number): number {
  const [x, z] = sentidoAFavorDoVento(direcaoGraus)
  return Math.atan2(x, z)
}

const PONTOS_CARDEAIS = ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO'] as const

/** "L" para 90°, "SO" para 225° — rosa de oito pontos, em português. */
export function pontoCardeal(direcaoGraus: number): string {
  return PONTOS_CARDEAIS[Math.round(normalizarGraus(direcaoGraus) / 45) % 8]!
}

/** m/s → km/h. */
export const msParaKmh = (ms: number): number => ms * 3.6

/**
 * Faixa de vento da Tabela 3 do GRE: baixo < 6 mph (≈ 2,7 m/s), moderado
 * 6–12 mph, alto > 12 mph (≈ 5,4 m/s).
 */
export function faixaVentoGre(velocidadeMS: number): 'baixo' | 'moderado' | 'alto' {
  const mph = velocidadeMS / 0.44704
  if (mph < 6) return 'baixo'
  if (mph <= 12) return 'moderado'
  return 'alto'
}
